import {
  useEffect,
  type ReactNode,
  type MouseEvent,
  type CSSProperties,
  type JSX,
} from 'react';
import { getBrowserFeature } from '../../utils/getBrowserFeature';
import { type modalClasses } from './modal.classes';
import { ModalLargeOrMediumViewport } from './modalLargeOrMediumViewport';
import { ModalSmallViewport } from './modalSmallViewport';
import { useViewport } from '@doxyme/ui-foundation/hooks/useViewport';
import { AriaProps } from '@doxyme/ui-foundation/types';

export type ModalVerticalPosition = 'start' | 'center' | 'end';

/**
 * A dialog must have an accessible name (WCAG 4.1.2). Consumers must provide
 * either `aria-labelledby` (preferred — points at the visible heading id) or
 * `aria-label` (string fallback when there is no visible heading).
 */
type ModalLabelProps =
  | { 'aria-labelledby': string; 'aria-label'?: string }
  | { 'aria-labelledby'?: string; 'aria-label': string };

export type ModalProps = Omit<AriaProps, 'aria-labelledby' | 'aria-label'> &
  ModalLabelProps & {
    isOpen: boolean;
    className?: string;
    verticalPosition?: ModalVerticalPosition;
    disableTransition?: boolean;
    styles?: Partial<Record<keyof typeof modalClasses, CSSProperties>>;
    onClose?: (
      event: MouseEvent,
      reason: 'backdropClick' | 'escapeKeyDown',
    ) => void;
    children: ReactNode;
  };

const setKeyboardPolicy = (): void => {
  const { virtualKeyboard } = getBrowserFeature();
  if (virtualKeyboard) {
    virtualKeyboard.overlaysContent = true;
  }
};

/**
 * Modal provides the main container for the modal dialog, including a transition and backdrop.
 * Any contents are to be built using the exposed `<ModalHeader>`, `<ModalContent>`, `<ModalBody>`, `<ModalForm>` and `<ModalFooter>` components.
 */
export const Modal = (props: ModalProps): JSX.Element | null => {
  const { verticalPosition } = props;
  const { isSmallViewport, isStable } = useViewport();

  /**
   * Right now we have to support a case when modal looks just the same way as on the
   * large viewport *but positioned at the bottom instead of center.
   */
  const isSmallViewportWithoutPositioning =
    isSmallViewport && !Boolean(verticalPosition);

  useEffect(() => {
    setKeyboardPolicy();
  }, []);

  /**
   * When mounting a modal onto an Iframe the viewport width always evaluates to 0 for the first few renders.
   * We wait for stable viewport detection to prevent flicker between different modal components.
   */
  if (!isStable) {
    return null;
  }

  return isSmallViewportWithoutPositioning ? (
    <ModalSmallViewport {...props} />
  ) : (
    <ModalLargeOrMediumViewport {...props} />
  );
};
